/**
 * CardsCallback — landing route after the aggregator's BankID flow returns
 * the user with `?code=...&state=...`.
 *
 * `state` is the connection_id we generated in `initConnect`. `code` is
 * the authorization code we exchange for an active session. We fire the
 * exchange exactly once via `useEffect` + `useRef` guard and use
 * `mutateAsync` to drive a local state machine — depending directly on
 * `useMutation`'s `status` field proved flaky under StrictMode with the
 * tRPC v11 RC client (`status` stayed at `pending` after `onSuccess`
 * fired). Local state is the simplest robust contract.
 *
 * `completeConnect` is idempotent server-side: once the connection is
 * active, repeated calls return early. So we can safely re-fire on
 * refresh.
 */
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { t } from "../i18n";
import { trpc } from "../lib/trpc";
import { Button, Card, FieldMessage, Icon, Spinner, Stack } from "./ui";

type CallbackState =
  | { kind: "pending" }
  | { kind: "success"; accountCount: number; alreadyActive: boolean }
  | { kind: "error"; message: string };

export default function CardsCallback() {
  const search = useSearch({ from: "/cards/callback" }) as {
    code?: string;
    state?: string;
    error?: string;
  };
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const completeConnect = trpc.cardConnections.completeConnect.useMutation();
  const [state, setState] = useState<CallbackState>({ kind: "pending" });
  const firedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional fire-once
  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    if (search.error) {
      setState({ kind: "error", message: t.cards.callback.errorGeneric(search.error) });
      return;
    }
    if (!search.code || !search.state) {
      setState({ kind: "error", message: t.cards.callback.errorMissingParams });
      return;
    }
    completeConnect
      .mutateAsync({ connectionId: search.state, authCode: search.code })
      .then((result) => {
        const alreadyActive = "alreadyActive" in result && result.alreadyActive === true;
        const accountCount =
          "accountCount" in result && typeof result.accountCount === "number"
            ? result.accountCount
            : 0;
        setState({ kind: "success", accountCount, alreadyActive });
        // Refresh the cards list in the background; we don't await it.
        utils.cardConnections.list.invalidate();
      })
      .catch((err: Error) => {
        setState({ kind: "error", message: t.cards.callback.errorGeneric(err.message) });
      });
  }, []);

  if (state.kind === "error") return <ErrorCard message={state.message} />;
  if (state.kind === "success") {
    return (
      <SuccessCard
        accountCount={state.accountCount}
        alreadyActive={state.alreadyActive}
        onContinue={() => navigate({ to: "/profile/cards" })}
      />
    );
  }
  return <ProcessingCard />;
}

// ---------------------------------------------------------------------------
// Sub-views — kept inline so the state-machine above stays the focal point
// ---------------------------------------------------------------------------

function ProcessingCard() {
  return (
    <Layout>
      <Stack gap={3} className="items-center">
        <Spinner />
        <h1 className="font-semibold text-ink-800 text-lg">{t.cards.callback.processingTitle}</h1>
        <p className="text-center text-ink-500 text-sm">{t.cards.callback.processingDescription}</p>
      </Stack>
    </Layout>
  );
}

function SuccessCard({
  accountCount,
  alreadyActive,
  onContinue,
}: {
  accountCount: number;
  alreadyActive: boolean;
  onContinue: () => void;
}) {
  const description = alreadyActive
    ? t.cards.callback.successAlreadyActive
    : t.cards.callback.successDescription(accountCount);
  return (
    <Layout>
      <Stack gap={3} className="items-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
          <Icon name="check" size="lg" />
        </div>
        <h1 className="font-semibold text-ink-800 text-lg">{t.cards.callback.successTitle}</h1>
        <p className="text-center text-ink-500 text-sm">{description}</p>
        <Button variant="primary" onClick={onContinue}>
          {t.cards.callback.successAction}
        </Button>
      </Stack>
    </Layout>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <Layout>
      <Stack gap={3} className="items-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
          <Icon name="alert-circle" size="lg" />
        </div>
        <h1 className="font-semibold text-ink-800 text-lg">{t.cards.callback.errorTitle}</h1>
        <FieldMessage tone="error" className="text-center">
          {message}
        </FieldMessage>
        <Link to="/profile/cards">
          <Button>{t.cards.callback.retryAction}</Button>
        </Link>
      </Stack>
    </Layout>
  );
}

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-xl py-12">
      <Card padding="lg">{children}</Card>
    </div>
  );
}
